import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import {
  Col,
  Container,
  Row,
  Card,
  CardBody,
  CardHeader,
  Input,
  Button,
  UncontrolledTooltip,
} from "reactstrap";

import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import { useNavigate, useLocation } from "react-router-dom";
import BreadCrumb from "Components/Common/BreadCrumb";
import Loader from "Components/Common/Loader";

import { fetchFamilyDetailReportThunk } from "slices/Report/thunks";
import { exportToExcel } from "helpers/exportToExcel";
import { showSuccessToast, showErrorToast } from "helpers/toast_helper";
import { listFamilyDetailReport } from "services/reportService";

import FamilyDetailContainer from "./familydetailContainer";
import moment from "moment";

import type { RootState } from "store";
import type { ThunkDispatch } from "redux-thunk";
import type { AnyAction } from "redux";

const selectReportState = (state: RootState) => (state as any).report;

const reportSelector = createSelector(selectReportState, (report) => ({
  familyDetailList: report.familyDetailList,
  loading: report.loading,
  pagination: report.pagination,
}));

const FamilyDetailPage: React.FC = () => {
  const dispatch: ThunkDispatch<RootState, any, AnyAction> = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const isInitialMount = useRef(true);
  const prevPathname = useRef<string | null>(null);
  const isNavigatingBack = useRef(false);

  const { familyDetailList, loading, pagination } = useSelector(reportSelector);

  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [familyId, setFamilyId] = useState(0);
  const [phoneNo, setPhoneNo] = useState("");
  const [email, setEmail] = useState("");
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);

  const rawFamilyDetailData = familyDetailList?.familydetails || [];

  const transformedData = useMemo(() => {
    const rows: any[] = [];

    rawFamilyDetailData.forEach((familyMember: any) => {
      if (!familyMember.goal_detail || familyMember.goal_detail.length === 0) {
        rows.push({
          user_id: familyMember.user_id,
          famillyid: familyMember.famillyid,
          name: `${familyMember.first_name || ""} ${familyMember.middle_name || ""} ${familyMember.last_name || ""}`.trim(),
          relationship_name: familyMember.relationship_name || "",
          last_contribution: null,
          goalname: "-",
          contribution_date: null,
          goal_status: "-",
          originalData: familyMember,
        });
      } else {
        familyMember.goal_detail.forEach((goal: any) => {
          let lastContribution = null;
          let contributionDate = null;

          if (goal.contributor && goal.contributor.length > 0) {
            // Sort contributors by createdon date (newest first)
            const sortedContributors = [...goal.contributor].sort(
              (a: any, b: any) =>
                new Date(b.createdon).getTime() - new Date(a.createdon).getTime()
            );
            lastContribution = sortedContributors[0];
            contributionDate = lastContribution.createdon;
          }

          rows.push({
            user_id: familyMember.user_id,
            famillyid: familyMember.famillyid,
            name: `${familyMember.first_name || ""} ${familyMember.middle_name || ""} ${familyMember.last_name || ""}`.trim(),
            relationship_name: familyMember.relationship_name || "",
            last_contribution: lastContribution?.amount || null,
            goalname: goal.goalname || "-",
            contribution_date: contributionDate,
            goal_status: goal.goal_status || "-",
            // Keep original data for reference
            originalData: familyMember,
            goalData: goal,
          });
        });
      }
    });

    return rows;
  }, [rawFamilyDetailData]);

  // Apply search filter
  const filteredData = useMemo(() => {
    if (!searchQuery.trim()) return transformedData;

    const query = searchQuery.toLowerCase();
    return transformedData.filter((row: any) => {
      return (
        String(row.user_id || "").toLowerCase().includes(query) ||
        String(row.famillyid || "").toLowerCase().includes(query) ||
        String(row.name || "").toLowerCase().includes(query) ||
        String(row.relationship_name || "").toLowerCase().includes(query) ||
        String(row.goalname || "").toLowerCase().includes(query) ||
        String(row.goal_status || "").toLowerCase().includes(query)
      );
    });
  }, [transformedData, searchQuery]);

  // Call API immediately on component mount or when navigating back to this page
  useEffect(() => {
    const isNavigatingToThisPage = prevPathname.current !== null && 
                                   prevPathname.current !== location.pathname && 
                                   location.pathname === '/goalreport';
    
    // Call API immediately on initial mount or when navigating back to this page
    if (isInitialMount.current || isNavigatingToThisPage) {
      if (isNavigatingToThisPage) {
        isNavigatingBack.current = true;
        // Reset flag after a short delay to allow debounced effect to check it
        setTimeout(() => {
          isNavigatingBack.current = false;
        }, 100);
      }
      
      dispatch(
        fetchFamilyDetailReportThunk({
          familyid: familyId,
          phone_no: phoneNo,
          email: email,
          page: page,
          page_size: limit,
        })
      );
      isInitialMount.current = false;
      prevPathname.current = location.pathname;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, dispatch]); // Trigger on mount and pathname change

  // Debounced fetch (500ms) for filter/page changes - skip on initial mount and navigation back
  useEffect(() => {
    // Skip debounce on initial mount or when navigating back (handled by the effect above)
    if (isInitialMount.current || isNavigatingBack.current) {
      return;
    }

    const handler = setTimeout(() => {
      dispatch(
        fetchFamilyDetailReportThunk({
          familyid: familyId,
          phone_no: phoneNo,
          email: email,
          page: page,
          page_size: limit,
        })
      );
    }, 500);

    return () => clearTimeout(handler);
  }, [dispatch, page, limit, familyId, phoneNo, email]);

  useEffect(() => {
    setPage(1);
  }, [familyId, phoneNo, email]);

  const handlePageChange = (newPage: number) => setPage(newPage);

  const handleRowClick = useCallback((row: any) => {
    // Navigate to family goals page with family ID
    const familyId = row.famillyid || row.originalData?.famillyid || 0;
    navigate(`/familygoals?familyid=${familyId}`);
  }, [navigate]);

  const handleSearch = () => {
    setPage(1);
    dispatch(
      fetchFamilyDetailReportThunk({
        familyid: familyId,
        phone_no: phoneNo,
        email: email,
        page: 1,
        page_size: limit,
      })
    );
  };

  const handleApplyFilters = () => {
    setPage(1);
    setIsFilterPanelOpen(false);
    dispatch(
      fetchFamilyDetailReportThunk({
        familyid: familyId,
        phone_no: phoneNo,
        email: email,
        page: 1,
        page_size: limit,
      })
    );
  };

  const handleClearFilters = () => {
    setFamilyId(0);
    setPhoneNo("");
    setEmail("");
    setPage(1);
  };

  const handleDownload = useCallback(async () => {
    try {
      if (!pagination?.total_pages) {
        showErrorToast("Pagination data missing!");
        return;
      }

      showSuccessToast("Preparing full export...");

      let allFamilyDetails: any[] = [];

      // Loop through all pages & fetch each page directly from API
      for (let p = 1; p <= pagination.total_pages; p++) {
        const response = await listFamilyDetailReport({
          familyid: familyId,
          phone_no: phoneNo,
          email: email,
          page: p,
          page_size: limit,
        });

        if (response?.success && response?.data?.familydetails) {
          allFamilyDetails = [...allFamilyDetails, ...response.data.familydetails];
        }
      }

      if (allFamilyDetails.length === 0) {
        showErrorToast("No data available to export!");
        return;
      }

      // Transform the data for export
      const exportData: any[] = [];
      allFamilyDetails.forEach((familyMember: any) => {
        if (!familyMember.goal_detail || familyMember.goal_detail.length === 0) {
          exportData.push({
            "User ID": familyMember.user_id,
            "Family ID": familyMember.famillyid,
            "Name": `${familyMember.first_name || ""} ${familyMember.middle_name || ""} ${familyMember.last_name || ""}`.trim(),
            "Relationship": familyMember.relationship_name || "",
            "Last Contribution": "-",
            "Goal Name": "-",
            "Contribution Date": "-",
            "Goal Status": "-",
          });
        } else {
          familyMember.goal_detail.forEach((goal: any) => {
            let lastContribution = null;
            let contributionDate = null;

            if (goal.contributor && goal.contributor.length > 0) {
              const sortedContributors = [...goal.contributor].sort(
                (a: any, b: any) =>
                  new Date(b.createdon).getTime() - new Date(a.createdon).getTime()
              );
              lastContribution = sortedContributors[0];
              contributionDate = lastContribution.createdon;
            }

            exportData.push({
              "User ID": familyMember.user_id,
              "Family ID": familyMember.famillyid,
              "Name": `${familyMember.first_name || ""} ${familyMember.middle_name || ""} ${familyMember.last_name || ""}`.trim(),
              "Relationship": familyMember.relationship_name || "",
              "Last Contribution": lastContribution?.amount || "-",
              "Goal Name": goal.goalname || "-",
              "Contribution Date": contributionDate ? moment(contributionDate).format("YYYY-MM-DD") : "-",
              "Goal Status": goal.goal_status || "-",
            });
          });
        }
      });

      exportToExcel(exportData, "Family_Detail_Report");
      showSuccessToast("Family detail report exported successfully!");
    } catch (error: any) {
      const message =
        error?.message ||
        error?.response?.data?.message ||
        "Failed to export family details";
      showErrorToast(message);
    }
  }, [pagination, limit, familyId, phoneNo, email]);

  const handleFilter = () => {
    setIsFilterPanelOpen(true);
  };

  const formatDate = (d: string) => {
    if (!d) return "-";
    return moment(d).format("YYYY-MM-DD");
  };

  const getGoalStatusBadge = (status: string) => {
    if (!status || status === "-") return "-";
    
    const value = status;
    const lowerVal = value?.toString().toLowerCase();

    const statusColor = lowerVal === "active" ? "#159E3A" : "#A65F00"; // Green / Orange

    return (
      <span
        style={{
          display: "inline-block",
          padding: "4px 12px",
          borderRadius: "20px",
          fontSize: "12px",
          fontWeight: "500",
          color: statusColor,
          border: `1px solid ${statusColor}`,
          textTransform: "capitalize",
        }}
      >
        {value}
      </span>
    );
  };

  const columns = useMemo(
    () => [
      { header: "User ID", accessorKey: "user_id" },
      { header: "Family ID", accessorKey: "famillyid" },
      { header: "Name", accessorKey: "name" },
      { header: "Relationship w/h", accessorKey: "relationship_name" },
      {
        header: "Last contribution",
        accessorKey: "last_contribution",
        cell: (cell: any) => {
          const amount = cell.getValue();
          return amount !== null && amount !== undefined
            ? `${amount?.toFixed(2) || "0.00"}`
            : "-";
        },
      },
      { header: "Goal name", accessorKey: "goalname" },
      {
        header: "Contribution Date",
        accessorKey: "contribution_date",
        cell: (cell: any) => formatDate(cell.getValue()),
      },
      {
        header: "Goal Status",
        accessorKey: "goal_status",
        cell: (cell: any) => getGoalStatusBadge(cell.getValue()),
      },
    ],
    []
  );

  document.title = "Family Member";

  return (
    <div className="page-content">
      <Container fluid className="border pt-4 rounded-4 bg-light mb-3 ms-3 pe-4">
        <BreadCrumb title="Family Member" pageTitle={["Report", "Family Member"]} />

        <Row className="align-items-center g-3 d-flex justify-content-between pb-4 pe-3">
          <Col md={3}>
            <div className="position-relative">
              <Input
                type="text"
                className="form-control border ps-5 ms-2 rounded-3"
                value={searchQuery}
                placeholder="Search.."
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    handleSearch();
                  }
                }}
              />
              <i
                className="ri-search-line position-absolute top-50 start-0 translate-middle-y text-muted ms-3"
                style={{ fontSize: "1.1rem" }}
              />
            </div>
          </Col>

          <Col md={7} className="text-end d-flex justify-content-end gap-2 align-items-center">
            {/* Download Button */}
            <Button
              id="download-btn-familydetail"
              color="primary"
              outline
              type="button"
              onClick={handleDownload}
              size="sm"
              style={{ minWidth: "40px", padding: "0.375rem 0.5rem" }}
            >
              <i className="ri-download-line" style={{ fontSize: "18px" }}></i>
            </Button>
            <UncontrolledTooltip 
              placement="top" 
              target="download-btn-familydetail"
            >
              <span style={{ color: 'white' }}>Download</span>
            </UncontrolledTooltip>
            {/* Filter Button */}
            <Button
              id="filter-btn-familydetail"
              color="secondary"
              outline
              type="button"
              onClick={handleFilter}
              size="sm"
              style={{ minWidth: "40px", padding: "0.375rem 0.5rem" }}
            >
              <i className="ri-filter-line" style={{ fontSize: "18px" }}></i>
            </Button>
            <UncontrolledTooltip 
              placement="top" 
              target="filter-btn-familydetail"
            >
              <span style={{ color: 'white' }}>Filter</span>
            </UncontrolledTooltip>
          </Col>
        </Row>

        <Row>
          <Col lg={12}>
            <Card id="familyDetailList" className="border-0 shadow-none" style={{ border: "none" }}>
              <CardBody className="pt-3" style={{ minHeight: "500px" }}>
                <div className="tab-content">
                  {!loading ? (
                    filteredData?.length > 0 ? (
                      <FamilyDetailContainer
                        columns={columns}
                        data={filteredData}
                        pagination={pagination}
                        limit={limit}
                        setLimit={setLimit}
                        setPage={setPage}
                        onPageChange={(p) => setPage(p)}
                        onRowClick={handleRowClick}
                        tableClass="align-middle border-bottom rounded-pill"
                        theadClass="bg-primary text-white border-top border-success"
                        trClass=""
                        divClass="table-responsive table-card"
                      />
                    ) : (
                      <div className="text-center p-4">No records found.</div>
                    )
                  ) : (
                    <Loader />
                  )}
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Filter Panel */}
      {isFilterPanelOpen && (
        <>
          <div
            className="filter-overlay"
            onClick={() => setIsFilterPanelOpen(false)}
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.3)",
              zIndex: 1040,
              transition: "opacity 0.3s ease",
            }}
          />
          <div
            className="filter-panel"
            style={{
              position: "fixed",
              top: 0,
              right: isFilterPanelOpen ? 0 : "-25%",
              width: "25%",
              height: "100vh",
              backgroundColor: "#fff",
              boxShadow: "-2px 0 10px rgba(0, 0, 0, 0.1)",
              zIndex: 1050,
              transition: "right 0.3s ease",
              overflowY: "auto",
            }}
          >
            <Card className="border-0" style={{ height: "auto", minHeight: "100vh" }}>
              <CardHeader
                className="d-flex justify-content-between align-items-center bg-primary text-white"
                style={{
                  borderBottom: "1px solid #dee2e6",
                  padding: "1rem 1.25rem",
                }}
              >
                <h5 className="mb-0 text-white fw-semibold">Filter Family Details</h5>
                <Button
                  className="text-white"
                  onClick={() => setIsFilterPanelOpen(false)}
                  style={{
                    opacity: 1,
                    background: "transparent",
                    border: "none",
                    padding: "0.25rem 0.5rem",
                    fontSize: "1.25rem",
                  }}
                >
                  <i className="ri-close-line"></i>
                </Button>
              </CardHeader>
              <CardBody className="p-4">
                <div className="d-flex flex-column gap-4">
                  <div>
                    <label className="form-label fw-semibold mb-2" style={{ color: "#495057", fontSize: "0.875rem" }}>
                      Family ID
                    </label>
                    <Input
                      type="number"
                      value={familyId || ""}
                      onChange={(e) => setFamilyId(Number(e.target.value) || 0)}
                      placeholder="Enter Family ID"
                      className="form-control"
                    />
                  </div>
                  <div>
                    <label className="form-label fw-semibold mb-2" style={{ color: "#495057", fontSize: "0.875rem" }}>
                      Phone No
                    </label>
                    <Input
                      type="text"
                      value={phoneNo}
                      onChange={(e) => setPhoneNo(e.target.value)}
                      placeholder="Enter Phone No"
                      className="form-control"
                    />
                  </div>
                  <div>
                    <label className="form-label fw-semibold mb-2" style={{ color: "#495057", fontSize: "0.875rem" }}>
                      Email
                    </label>
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter Email"
                      className="form-control"
                    />
                  </div>
                </div>

                <div className="d-flex gap-3 mt-5 pt-4 border-top">
                  <Button
                    color="secondary"
                    className="flex-fill"
                    onClick={handleClearFilters}
                    style={{
                      borderRadius: "8px",
                      padding: "0.625rem",
                      fontWeight: "500",
                      fontSize: "0.9375rem",
                    }}
                  >
                    Clear
                  </Button>
                  <Button
                    color="primary"
                    className="flex-fill"
                    onClick={handleApplyFilters}
                    style={{
                      borderRadius: "8px",
                      padding: "0.625rem",
                      fontWeight: "500",
                      fontSize: "0.9375rem",
                    }}
                  >
                    Apply
                  </Button>
                </div>
              </CardBody>
            </Card>
          </div>
        </>
      )}
    </div>
  );
};

export default FamilyDetailPage;

